/**
 * Web Storage access that survives a browser refusing it.
 *
 * `localStorage` / `sessionStorage` are not optional-chainable away: when a
 * browser blocks site data (Safari "Block All Cookies", Chromium third-party
 * storage partitioning, some in-app browsers), reading the *property itself*
 * throws a SecurityError DOMException — `typeof localStorage` and
 * `"localStorage" in window` throw too, so a truthiness guard doesn't help.
 * Only try/catch does.
 *
 * That matters because an unguarded call inside a `useEffect` doesn't degrade
 * quietly: React rethrows from the passive-effect commit and the App Router
 * global error boundary replaces the whole page.
 *
 * Storage here is best-effort. A blocked write returns false and the caller
 * keeps its in-memory value for the session.
 */

type StorageKind = "localStorage" | "sessionStorage";

export interface SafeStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => boolean;
  removeItem: (key: string) => boolean;
}

function createSafeStorage(kind: StorageKind): SafeStorage {
  return {
    getItem(key) {
      try {
        return window[kind].getItem(key);
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      try {
        window[kind].setItem(key, value);
        return true;
      } catch {
        return false;
      }
    },
    removeItem(key) {
      try {
        window[kind].removeItem(key);
        return true;
      } catch {
        return false;
      }
    },
  };
}

export const safeLocalStorage = createSafeStorage("localStorage");
export const safeSessionStorage = createSafeStorage("sessionStorage");
